import { LOG_LEVEL, Purchases } from '@revenuecat/purchases-capacitor';
import { PAYWALL_RESULT, RevenueCatUI } from '@revenuecat/purchases-capacitor-ui';
import { Capacitor } from '@capacitor/core';
import { Sentry } from './sentry';

const ENTITLEMENT_ID = 'Protipster Premium';
let isConfigured = false;

export const isRevenueCatConfigured = () => isConfigured;

/**
 * Configure RevenueCat SDK with a user ID
 * Should be called once after user authentication
 * @param {string} userId - The user ID to associate with RevenueCat
 */
export const configureRevenueCat = async (userId) => {
  if (!userId) {
    console.error('❌ Cannot configure RevenueCat: userId is required');

    throw new Error('userId is required for RevenueCat configuration');
  }

  if (isConfigured) {
    console.log('ℹ️ RevenueCat already configured, skipping');

    return;
  }

  try {
    await Purchases.setLogLevel({ level: LOG_LEVEL.DEBUG });

    const platform = Capacitor.getPlatform();
    const apiKey = platform === 'ios'
      ? import.meta.env.VITE_REVENUECAT_IOS_API_KEY
      : import.meta.env.VITE_REVENUECAT_ANDROID_API_KEY;

    if (!apiKey) {
      console.warn(`⚠️ RevenueCat API key not found for platform: ${platform}`);

      return;
    }

    // Convert userId to string (RevenueCat requires string app user IDs)
    const appUserID = String(userId);

    await Purchases.configure({
      apiKey,
      appUserID,
    });

    isConfigured = true;

    console.log('✅ RevenueCat configured with user ID:', appUserID);
  } catch (error) {
    console.error('❌ Failed to configure RevenueCat:', error);

    throw error;
  }
};

/**
 * Set subscriber attributes (email, display name, etc.)
 * Should be called after configureRevenueCat
 * @param {Object} attributes - Attributes to set (e.g., { email, displayName })
 */
export const setSubscriberAttributes = async (attributes) => {
  if (!isConfigured) {
    return;
  }

  try {
    const attrs = {};

    if (attributes.email) {
      attrs.$email = attributes.email;
    }

    if (attributes.displayName) {
      attrs.$displayName = attributes.displayName;
    }

    // Add any custom attributes
    Object.keys(attributes).forEach(key => {
      if (key !== 'email' && key !== 'displayName') {
        attrs[key] = attributes[key];
      }
    });

    if (Object.keys(attrs).length > 0) {
      await Purchases.setAttributes(attrs);

      console.log('✅ Subscriber attributes set');
    }
  } catch (error) {
    console.error('❌ Failed to set subscriber attributes:', error);

    Sentry.captureException(error);
  }
};

/**
 * Check if user has active premium entitlement
 * @returns {Promise<boolean>}
 */
export const hasPremiumAccess = async () => {
  if (!isConfigured) {
    return false;
  }

  try {
    const { customerInfo } = await Purchases.getCustomerInfo();

    return typeof customerInfo.entitlements.active[ENTITLEMENT_ID] !== 'undefined';
  } catch (error) {
    console.error('❌ Error checking entitlement:', error);

    Sentry.captureException(error);

    return false;
  }
};

/**
 * Present the paywall to user (only if they don't have the entitlement)
 * Auto-dismisses after successful purchase or restore
 * @returns {Promise<boolean>} True if user has premium access, false otherwise
 */
export const presentPaywall = async () => {
  if (!isConfigured) {
    return false;
  }

  try {
    const { result } = await RevenueCatUI.presentPaywallIfNeeded({
      requiredEntitlementIdentifier: ENTITLEMENT_ID,
    });

    if (result === PAYWALL_RESULT.PURCHASED) {
      return true;
    }

    // Restore completed - verify entitlement is actually active
    if (result === PAYWALL_RESULT.RESTORED) {
      return await hasPremiumAccess();
    }

    return false;
  } catch (error) {
    console.error('❌ Error presenting paywall:', error);

    Sentry.captureException(error);

    return false;
  }
};

/**
 * Present the customer center to user
 * @returns {Promise<boolean>}
 */
export const presentCustomerCenter = async () => {
  if (!isConfigured) {
    return false;
  }

  try {
    await RevenueCatUI.presentCustomerCenter();
  } catch (error) {
    console.error('❌ Error presenting customer center:', error);

    Sentry.captureException(error);

    return false;
  }
};

/**
 * Get customer info
 * @returns {Promise<Object|null>}
 */
export const getCustomerInfo = async () => {
  if (!isConfigured) {
    return null;
  }

  try {
    const { customerInfo } = await Purchases.getCustomerInfo();

    return customerInfo;
  } catch (error) {
    console.error('❌ Error fetching customer info:', error);

    Sentry.captureException(error);

    return null;
  }
};

/**
 * Restore purchases
 * @returns {Promise<boolean>}
 */
export const restorePurchases = async () => {
  if (!isConfigured) {
    return false;
  }

  try {
    const { customerInfo } = await Purchases.restorePurchases();

    return typeof customerInfo.entitlements.active[ENTITLEMENT_ID] !== 'undefined';
  } catch (error) {
    console.error('❌ Error restoring purchases:', error);

    Sentry.captureException(error);

    return false;
  }
};
